//
// main.ts — assembly
//
// Translation of `ContentView.swift`: takes the map, the dial and the
// chrome, and decides where on screen each goes.
//
// The layout rule, settled in note.md after two wrong builds and unchanged
// since: **one layout for every device and orientation.** No orientation
// branch, no breakpoint that swaps to a different arrangement.
//
// **What build 1.5 item 9 changed:** the dial used to reserve a vertical
// band down one side, and the map was inset clear of it — which on a phone
// left the disc small and shoved into a corner. Dave: *"make the header all
// the way across and the menu a dynamic entity, able to be moved, just like
// the akaspace web-app one."* So now the header spans the full width, the
// map takes everything under it and is centred, and the arc menu **floats
// over** the map from the bottom edge rather than carving a column out of
// it. The margin setting moves the chrome column and the menu together.
//

import "./styles.css";
import { AppState } from "./state";
import { GleasonRenderer } from "./map/renderer";
import { ArcMenu, type MenuDock } from "./ui/arc";
import { Chrome } from "./ui/chrome";

const app = document.getElementById("app");
if (!app) throw new Error("AZISUR: #app not found in index.html");

const state = new AppState();

// --- structure ---
const mapLayer = document.createElement("div");
mapLayer.className = "map-layer";
const canvas = document.createElement("canvas");
canvas.className = "map-canvas";
mapLayer.appendChild(canvas);

const chromeLayer = document.createElement("div");
chromeLayer.className = "chrome-layer";

// A soft gradient behind the telemetry column. See `.hud-scrim` in
// styles.css for why this exists and why it is not a panel.
const scrim = document.createElement("div");
scrim.className = "hud-scrim";

const arcLayer = document.createElement("div");
arcLayer.className = "arc-layer";

app.appendChild(mapLayer);
app.appendChild(scrim);
app.appendChild(chromeLayer);
app.appendChild(arcLayer);

const renderer = new GleasonRenderer(canvas, state);
const chrome = new Chrome(chromeLayer, state);
const arc = new ArcMenu(arcLayer, state, {
  onSettings: () => {
    state.isSettingsOpen = !state.isSettingsOpen;
    state.notify();
  },
  onDock: (dock) => applyDock(dock),
});

// The Settings sheet's margin row and the arc's own P-placement have to end
// up in the same place, so the row routes back through the arc rather than
// setting the state behind its back.
chrome.onDockRequest = (side) => arc.setDock(side);

/**
 * The margin side — item 10. Dave: *"let's make a settings toggle for
 * right-left marginal display. And if switched, the menu swaps sides too."*
 * One setting moves the telemetry column and the arc together, so they are
 * never on opposite sides of the screen.
 */
function applyDock(dock: MenuDock): void {
  document.documentElement.style.setProperty("--dock-side", dock);
  state.dockSide = dock;

  chromeLayer.dataset.dock = dock;
  scrim.style.left = dock === "left" ? "0" : "auto";
  scrim.style.right = dock === "right" ? "0" : "auto";
  scrim.style.setProperty("--scrim-fade", dock === "right" ? "left" : "right");
  renderer.resize();
}

function applyLayout(): void {
  // The map gets everything under the header. It is no longer inset for a
  // band, so the disc is centred and as large as the window allows.
  renderer.resize();
  arc.paint();
}

window.addEventListener("resize", applyLayout);
applyLayout();

// --- the render loop ---
// The canvas redraws every frame the way `TimelineView(.animation)` drives
// the SwiftUI `Canvas`: wind particles move, satellites propagate, aircraft
// dead-reckon. The DOM chrome does *not* redraw per frame — it updates only
// when state changes, via the subscribe/notify list.
function frame(): void {
  renderer.render();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// --- lifecycle ---
state.startClockLoop();
state.startTimelineLoop();
state.startAircraftRefreshLoop();
void state.loadTelemetry();

// Handy for poking at the live instrument from a browser console.
(window as unknown as { azisur: AppState }).azisur = state;
